import { useState } from "react";
import type { ButtonProps, Omittable } from "@salestrak/ui";

import { UpdateAgeDialog } from "#app/components/age/update-age-dialog";
import {
  StartFollowUpButton,
  UpdateFollowUpButton,
} from "#app/components/follow-ups/follow-up-chip";
import { ProjectStartFollowUpDialog } from "#app/components/updates/project-start-follow-up-dialog";
import type { IssueRow } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { effectiveProjectCycle, type StagePin } from "#app/lib/project-updates";

/**
 * The Project detail header's follow-up verb — the one slot at the end of the
 * tab strip (and beside the mobile sheet's title) that is always the
 * follow-up's next action, so it never blinks in and out:
 *
 * - updates running → the primary "Update", opening the post-Update dialog;
 * - updates off (the Project, its Stage pin or the Workflow default says so)
 *   → the primary "Start Follow up", opening the Start dialog that turns
 *   them on for this one Project.
 *
 * The Follow up section's header summons a second, outline copy on hover — a
 * shortcut for a pointer already on the section, never the standing verb.
 * Read-only viewers get nothing: both actions write. Owns its own dialog
 * mount rather than borrowing the summary's, because the header outlives the
 * Summary tab and the mobile sheet has no summary-owned dialog to borrow.
 */
export function ProjectFollowUpAction({
  issue,
  stage,
  editable,
  variant = "solid",
}: Omittable<{
  issue: IssueRow;
  /** The resolved Stage when the host already has it (the dialog body
   *  resolves it once for every tab); omitted, the workspace stage map
   *  answers. */
  stage?: StagePin | null;
  editable: boolean;
  /** The button rung both states wear — the filled primary in the header,
   *  `outline` for the Follow up section's hover shortcut. */
  variant?: ButtonProps["variant"];
}>) {
  const { stageMap, workflowMap } = useWorkspaceData();
  const [open, setOpen] = useState(false);

  if (!editable) return null;

  const resolvedStage = stage ?? stageMap.get(issue.stageId) ?? null;
  const workflow = workflowMap.get(issue.workflowId) ?? null;
  const updatesOff =
    effectiveProjectCycle(issue.age, resolvedStage, workflow).cycle == null;

  if (updatesOff) {
    return (
      <>
        <StartFollowUpButton size="xs" variant={variant} onClick={() => setOpen(true)} />
        <ProjectStartFollowUpDialog
          via="detail_header"
          age={issue.age}
          issueId={issue.issueId}
          onOpenChange={setOpen}
          open={open}
          stage={resolvedStage}
          workflow={workflow}
        />
      </>
    );
  }

  return (
    <>
      <UpdateFollowUpButton variant={variant} onClick={() => setOpen(true)} />
      <UpdateAgeDialog
        age={issue.age}
        issueId={issue.issueId}
        onOpenChange={setOpen}
        open={open}
        stageId={issue.stageId}
        via="detail_header"
      />
    </>
  );
}
